import {
  pickRepresentativeConversation,
  toJsDate,
} from '../../services/encounterUtils';

const SUMMARY_PLACEHOLDERS = new Set([
  'Healthcare Conversation',
  'Healthcare conversation',
]);

const clean = (value: any) => String(value ?? '').trim();

const compact = (values: any[]) => values.map(clean).filter(Boolean);

const languageLabel = (...values: any[]) =>
  values.map(clean).find(Boolean) || '';

const titleCase = (value: any) =>
  clean(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());

const joinDetails = (...values: any[]) => compact(values).join(' · ');

const isEmailLike = (value: any) => /@/.test(clean(value));

export function isPlaceholderEncounterSummary(value: any) {
  return SUMMARY_PLACEHOLDERS.has(value);
}

export function encounterDisplayDate(encounter: any) {
  return toJsDate(encounter?.end_time) || toJsDate(encounter?.start_time);
}

export function encounterLanguage(encounter: any) {
  const conversation = Array.isArray(encounter?.conversations)
    ? encounter.conversations.find(Boolean)
    : null;
  return languageLabel(
    encounter?.foreign_language,
    encounter?.foreignLanguage,
    encounter?.language,
    conversation?.foreign_language,
    conversation?.foreignLanguage,
    conversation?.language,
  );
}

export function recordedMinutesFromValue(value: any) {
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

export function recordedMinutesFromSeconds(...values: any[]) {
  const seconds = values
    .map(Number)
    .find(value => Number.isFinite(value) && value > 0);
  return seconds ? seconds / 60 : 0;
}

export function recordedMinutesFromDateRange(start: any, end: any) {
  const startDate = toJsDate(start);
  const endDate = toJsDate(end);
  if (!startDate || !endDate) return 0;
  const minutes = (endDate.getTime() - startDate.getTime()) / 60000;
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

export function conversationRecordedMinutes(conversation: any) {
  return (
    recordedMinutesFromValue(conversation?.minutes) ||
    recordedMinutesFromSeconds(
      conversation?.elapsed_seconds,
      conversation?.elapsedSeconds,
    )
  );
}

export function encounterRecordedMinutes(encounter: any) {
  const conversations = Array.isArray(encounter?.conversations)
    ? encounter.conversations
    : [];
  const fromConversations = conversations.reduce(
    (sum: any, conversation: any) =>
      sum + conversationRecordedMinutes(conversation),
    0,
  );
  const explicit =
    fromConversations ||
    recordedMinutesFromValue(encounter?.total_minutes) ||
    recordedMinutesFromValue(encounter?.minutes) ||
    recordedMinutesFromSeconds(
      encounter?.elapsed_seconds,
      encounter?.elapsedSeconds,
    ) ||
    recordedMinutesFromDateRange(encounter?.start_time, encounter?.end_time);
  return explicit > 0 ? Math.max(1, Math.ceil(explicit)) : 0;
}

export function encounterStartedByViewer(encounter: any, viewerUserId: any) {
  if (!viewerUserId) return false;
  const contributor = (
    Array.isArray(encounter?.contributors) ? encounter.contributors : []
  ).find(Boolean);
  const conversation = Array.isArray(encounter?.conversations)
    ? encounter.conversations.find(Boolean)
    : null;
  const starterId = clean(contributor?.user_id) || clean(conversation?.user_id);
  return Boolean(starterId) && String(starterId) === String(viewerUserId);
}

export function encounterProviderLabel(encounter: any, viewerUserId: any) {
  const contributors = Array.isArray(encounter?.contributors)
    ? encounter.contributors
    : [];
  if (contributors.length > 1) return `${contributors.length} contributors`;
  if (encounterStartedByViewer(encounter, viewerUserId)) return '';
  const contributor = contributors.find(Boolean);
  const conversation = Array.isArray(encounter?.conversations)
    ? encounter.conversations.find(Boolean)
    : null;
  const provider =
    clean(contributor?.provider_name) ||
    clean(contributor?.providerName) ||
    clean(conversation?.provider_name) ||
    clean(conversation?.providerName) ||
    '';
  if (!provider || isEmailLike(provider)) return '';
  return `Started by ${provider}`;
}

export function encounterDetailLine(encounter: any, viewerUserId: any) {
  return joinDetails(
    encounterProviderLabel(encounter, viewerUserId),
    titleCase(encounter?.specialty || encounter?.specialty_name),
  );
}

export function representativeEncounterSession(
  encounter: any,
  viewerUserId: any,
) {
  const picked = pickRepresentativeConversation(encounter, viewerUserId);
  return picked ? {...picked, __encounterContext: encounter} : null;
}
